import { interpolateDatasets } from "@helpers/interpolateDatasets"
import { chartOptionsMultiAxis } from "@ui-lib/core/chartJS/chartOptionsMultiAxis"
import { LinePlotDataType } from "@ui-lib/core/chartJS/types"
import { LoadingCircle } from "@ui-lib/index"
import {
	COLORS_PLOTS_COLORBLIND as COLORS_VESSEL_COMPARISON,
	isColorBright,
} from "@utils/colorsPlots"
import { ChartData, ChartOptions } from "chart.js"
import { useEffect, useState } from "react"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"

export default function SeempYtdTrendPlot({
	trends,
	target,
	targetUnit,
	targetYear,
}: {
	trends:
		| {
				value: number
				trends: {
					trends: {
						label: string
						data: [Date, number][]
					}[]
					unit: string
					yAxisLabel: string
				}[]
		  }
		| null
		| undefined
	target: number | null
	targetUnit: string
	targetYear: number
}) {
	const { t } = useTranslation()

	const [trendPlotData, setTrendPlotData] = useState<ChartData<"line", LinePlotDataType[]>>()
	const [trendPlotOptions, setTrendPlotOptions] = useState<ChartOptions<"line">>()

	const trendGroups = trends?.trends ?? []
	const hasSecondaryAxis = trendGroups.length > 1

	useEffect(() => {
		if (trends === null || trendGroups.length === 0) return

		// Helper to interpolate one group's sensor datasets
		const interpolateGroup = (groupIndex: number) => {
			const group = trendGroups[groupIndex]
			if (group === undefined || group === null) return []
			const datasetsToInterp = group.trends
				.map((serie) => ({ id: serie.label, data: serie.data }))
				.filter((d) => d.data.length > 0)
			return interpolateDatasets(
				datasetsToInterp.map((d) => ({
					id: d.id,
					data: d.data.filter((dp): dp is [Date, number] => dp[1] !== null),
				}))
			)
		}

		const primaryInterpolated = interpolateGroup(0)
		const secondaryInterpolated = hasSecondaryAxis ? interpolateGroup(1) : []

		// Labels from first interpolated dataset of primary axis
		const labels = primaryInterpolated[0]?.dataPoints.map((d) => d.timestamp) ?? []

		// Combine units for tooltip mapping (include both axes datasets)
		const allUnits: { label: string; unit: string }[] = [
			...primaryInterpolated.map((ds) => ({
				label: String(ds.sensorId),
				unit: `${trendGroups[0]!.unit === "%" || trendGroups[0]!.unit === "" ? "" : " "}${trendGroups[0]!.unit}`,
			})),
			...secondaryInterpolated.map((ds) => ({
				label: String(ds.sensorId),
				unit: `${trendGroups[1]!.unit === "%" || trendGroups[1]!.unit === "" ? "" : " "}${trendGroups[1]!.unit}`,
			})),
		]

		const tempOptions: ChartOptions<"line"> = chartOptionsMultiAxis(
			"line",
			`${trendGroups[0]!.yAxisLabel} (${trendGroups[0]!.unit})`,
			hasSecondaryAxis
				? `${trendGroups[1]!.yAxisLabel} (${trendGroups[1]!.unit})`
				: undefined,
			undefined,
			allUnits,
			true,
			{ crosshair: { enabled: true }, sync: { enabled: true, group: "prevYearTrend" } }
		)

		// Ensure secondary axis visibly separated on right with explicit title and padding so label is visible
		const y1Scale = tempOptions.scales?.y1
		if (hasSecondaryAxis && y1Scale !== undefined && y1Scale !== null) {
			// @ts-ignore
			y1Scale.position = "left"
			y1Scale.title = {
				...(y1Scale.title ?? {}),
				display: true,
				text: `${trendGroups[1]!.yAxisLabel} (${trendGroups[1]!.unit})`,
				font: { size: 14 },
			}
		}

		// Add layout padding on right so axis title isn't clipped
		const existingPadding = tempOptions.layout?.padding
		const existingPaddingRaw = tempOptions.layout?.padding
		let existingPaddingObj: { top?: number; left?: number; bottom?: number; right?: number } =
			{}
		if (
			typeof existingPaddingRaw === "object" &&
			existingPaddingRaw !== null &&
			!Array.isArray(existingPaddingRaw)
		) {
			existingPaddingObj = existingPaddingRaw as typeof existingPaddingObj
		}
		tempOptions.layout = {
			...(tempOptions.layout ?? {}),
			padding: {
				top: existingPaddingObj.top ?? 0,
				left: existingPaddingObj.left ?? 0,
				bottom: existingPaddingObj.bottom ?? 0,
				right:
					existingPaddingObj.right !== undefined
						? Math.max(24, existingPaddingObj.right)
						: 24,
			},
		}

		const tempData: ChartData<"line", LinePlotDataType[]> = { datasets: [], labels }

		primaryInterpolated.forEach((dataset, idx) => {
			const dPoints = dataset.dataPoints
			tempData.datasets.push({
				label: String(dataset.sensorId),
				data: dPoints.map((d) => ({ x: d.timestamp, y: d.value })),
				borderColor:
					primaryInterpolated.length > 1
						? COLORS_VESSEL_COMPARISON[idx + 1]
						: COLORS_VESSEL_COMPARISON[0],
				backgroundColor:
					primaryInterpolated.length > 1
						? COLORS_VESSEL_COMPARISON[idx + 1]
						: COLORS_VESSEL_COMPARISON[0],
				borderWidth: 2,
				pointRadius: 2,
				showLine: true,
				yAxisID: "y",
				parsing: { xAxisKey: "x" },
			})
		})

		secondaryInterpolated.forEach((dataset, idx) => {
			const dPoints = dataset.dataPoints
			tempData.datasets.push({
				label: String(dataset.sensorId),
				data: dPoints.map((d) => ({ x: d.timestamp, y: d.value })),
				borderColor:
					secondaryInterpolated.length > 1
						? COLORS_VESSEL_COMPARISON[idx + 2]
						: COLORS_VESSEL_COMPARISON[1],
				backgroundColor:
					secondaryInterpolated.length > 1
						? COLORS_VESSEL_COMPARISON[idx + 2]
						: COLORS_VESSEL_COMPARISON[1],
				borderWidth: 2,
				pointRadius: 2,
				showLine: true,
				yAxisID: "y1",
				parsing: { xAxisKey: "x" },
			})
		})

		setTrendPlotData(tempData)
		setTrendPlotOptions({
			...tempOptions,
			scales: {
				...tempOptions.scales,
				x: {
					...tempOptions.scales?.x,
					time: {
						unit: "month",
						round: "month",
						tooltipFormat: "MMM yyyy",
						displayFormats: { month: "MMM yyyy" },
					},
					ticks: {
						source: "auto",
						autoSkip: true,
						major: { enabled: true },
						callback: function (val) {
							const date = new Date(val)
							return date.getDate() === 1 ? this.getLabelForValue(val as number) : ""
						},
					},
				},
			},
			plugins: {
				...tempOptions.plugins,
				datalabels: {
					display: "auto",
					font: { size: 12, weight: 400 },
					color(context) {
						const bg = context.dataset.backgroundColor
						if (typeof bg === "string" && isColorBright(bg)) return "#222222"
						return "white"
					},
					backgroundColor(context) {
						const bg = context.dataset.backgroundColor
						if (typeof bg === "string") return bg
						return "white"
					},
					borderRadius: 2,
					borderWidth: 0,
					formatter(value: { x: Date; y: number }, context) {
						if (typeof value.y === "number") {
							const unitRef =
								allUnits.find((u) => u.label === context.dataset.label)?.unit ?? ""
							return `${value.y.toFixed(1)}${unitRef}`
						}
					},
					offset() {
						return 4
					},
					align(context) {
						const tempValue = (
							context.dataset.data[context.dataIndex] as { x: number; y: number }
						)?.y
						if (context.dataIndex === 0) return "left"
						if (context.dataIndex === 11) return "right"
						if (
							typeof tempValue === "number" &&
							tempValue >
								(2 *
									(context.chart.scales.y?.max ??
										Math.max(...(context.dataset.data as number[])))) /
									3
						) {
							return `start`
						}
						return "end"
					},
					anchor(context) {
						const tempValue = (
							context.dataset.data[context.dataIndex] as { x: number; y: number }
						)?.y
						if (
							typeof tempValue === "number" &&
							tempValue >
								(context.chart.scales.y?.max ??
									Math.max(...(context.dataset.data as number[]))) /
									2
						)
							return `center`
						return "end"
					},
					clamp: true,
					clip: true,
					padding: 3,
				},
				annotation:
					target !== null
						? {
								annotations: {
									...(target !== null
										? Object.fromEntries([
												[
													"target",
													{
														centerX: 0,
														type: "line",
														yMin: target,
														yMax: target,
														borderColor: "#333",
														borderWidth: 1,
														borderDash: [6, 6],
														label: {
															display: true,
															content: `${t("TARGET")}: ${target.toFixed(1)}${targetUnit === "%" || targetUnit === "" ? "" : " "}${targetUnit}`,
															position: "end",
															xAdjust: 0,
															yAdjust: 10,
															backgroundColor: "transparent",
															color: "#333",
															font: {
																weight: "bold",
																size: 12,
															},
														},
													},
												],
											])
										: {}),
								},
							}
						: undefined,
			},
		})
	}, [trends, trendGroups.length, hasSecondaryAxis])
	// TODO: add support for axis name, unit

	const hasPrimaryData = trendGroups.length > 0

	const showTrend = new Date(targetYear, 1, 1) < new Date(new Date())

	return hasPrimaryData ? (
		showTrend ? (
			trendPlotData !== undefined && trendPlotOptions !== undefined ? (
				<div className={`h-full min-h-80 w-full rounded-sm`}>
					<Line
						options={trendPlotOptions}
						data={trendPlotData}
					/>
				</div>
			) : (
				<div className={`relative h-90 w-full`}>
					<div
						className={`absolute top-0 h-full w-full animate-pulse rounded-lg bg-gray-300/80`}
					/>
					<LoadingCircle
						size={"medium"}
						containerHeightWithUnit="360px"
					/>
				</div>
			)
		) : (
			<p>{`${t("TREND DATA WILL BE AVAILABLE STARTING IN FEBRUARY")} ${targetYear}`}</p>
		)
	) : (
		<p className={`text-tiny text-dipai-secondary-901`}>
			{t("NO TREND DATA AVAILABLE FOR THE SELECTED ACTION AND/OR REPORT YEAR")}
		</p>
	)
}
